import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { ZodIssue, z } from 'zod';

import type { CreateHandoffSchema } from '@polyai/stream/commands/handlers/handoff/create-config/schema';
import type { UpdateHandoffSchema } from '@polyai/stream/commands/handlers/handoff/update-config/schema';
import {
  type Handoff,
  handoffReferences,
} from '@polyai/stream/store/reducers/handoff';
import { selectHandoffs } from '@polyai/stream/store/reducers/handoff/selectors';
import { CalloutType } from '@polyai/ui/components/atoms/Callout';
import type { ConfirmationModalProps } from '@polyai/ui/components/molecules/ConfirmationModal';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import FormProvider from 'components/molecules/Form/FormProvider';
import FormAddEditHandoffStreamModalContent from 'components/organisms/AddEditHandoffStreamModalContent';
import { useAppSelector } from 'hooks/reduxHooks';
import { getActionName } from 'lib/ddlogHelper';
import { generateHandoffId } from 'lib/prettyUuid';
import {
  DEFAULT_ENCRYPTION,
  DEFAULT_METHOD_TYPE,
  METHOD_TYPE_PREFIX,
} from 'screens/Handoff/constants';
import { HANDOFF } from 'screens/Handoff/Handoff.uiCopy';
import { useIntegrations } from 'screens/Integrations/hooks/useIntegrations';
import type { SipMethod } from 'utils/validators/handoff';
import { HandoffSchema } from 'utils/validators/handoff';

export type ModalSchema = z.input<typeof HandoffSchema>;
type Props<
  T extends
    | typeof UpdateHandoffSchema
    | typeof CreateHandoffSchema = typeof CreateHandoffSchema,
> = ConfirmationModalProps & {
  onConfirmClicked: (data: z.infer<T>) => void;
  readonly?: boolean;
  streamSchema: T;
  defaultValues?: Handoff;
};

const DEFAULT_VALUES_CREATE = {
  methodType: DEFAULT_METHOD_TYPE,
  name: '',
  description: '',
  method: {
    value: DEFAULT_METHOD_TYPE as SipMethod,
    label: `${METHOD_TYPE_PREFIX} ${DEFAULT_METHOD_TYPE.toUpperCase()}`,
  },
  sipHeaders: [{ key: '', value: '', isLast: true }],
  encryption: { value: DEFAULT_ENCRYPTION, label: DEFAULT_ENCRYPTION },
  integrationName: '',
  route: '',
};

const getDefaultValues = (
  defaultValues: Handoff | undefined,
  integrationDefaultMethod?: SipMethod,
) => {
  const sipConfig = defaultValues?.sipConfig?.config;

  if (!defaultValues || !sipConfig) {
    const method = integrationDefaultMethod ?? DEFAULT_METHOD_TYPE;
    return {
      ...{
        ...DEFAULT_VALUES_CREATE,
        methodType: method,
        method: {
          value: method,
          label: `${METHOD_TYPE_PREFIX} ${method.toUpperCase()}`,
        },
      },
      id: defaultValues?.id,
      name: defaultValues?.name,
      description: defaultValues?.description ?? '',
    } as ModalSchema;
  }

  const sipHeaders =
    defaultValues.sipHeaders?.headers.map(header => ({
      key: header.key,
      value: header.value,
      isLast: false,
    })) || [];

  const encryption =
    sipConfig?.$case === 'invite' && sipConfig?.value.outboundEncryption
      ? {
          value: sipConfig.value.outboundEncryption,
          label: sipConfig.value.outboundEncryption,
        }
      : { value: DEFAULT_ENCRYPTION, label: DEFAULT_ENCRYPTION };

  const integrationName =
    sipConfig?.$case === 'invite' && sipConfig?.value.outboundEndpoint
      ? sipConfig.value.outboundEndpoint
      : '';

  const route =
    (sipConfig?.$case === 'invite' || sipConfig?.$case === 'refer') &&
    sipConfig?.value.phoneNumber
      ? sipConfig.value.phoneNumber
      : '';

  return {
    id: defaultValues.id,
    name: defaultValues.name,
    methodType: sipConfig?.$case as SipMethod,
    method: {
      value: sipConfig?.$case as SipMethod,
      label: `${METHOD_TYPE_PREFIX} ${
        sipConfig?.$case?.toUpperCase() || DEFAULT_METHOD_TYPE.toUpperCase()
      }`,
    },
    sipHeaders: [...sipHeaders, { key: '', value: '', isLast: true }],
    encryption,
    integrationName,
    route,
    description: defaultValues.description || '',
  } as ModalSchema;
};

export const FormHandoffModal = <
  T extends typeof UpdateHandoffSchema | typeof CreateHandoffSchema,
>({
  onConfirmClicked,
  onCancelButtonClick,
  readonly = false,
  defaultValues,
  streamSchema,
  ...rest
}: Props<T>) => {
  const [errors, setErrors] = useState<ZodIssue[]>([]);
  const handoffs = useAppSelector(selectHandoffs);
  const [showCallout, setShowCallout] = useState(false);
  const { activeTelephonyIntegration } = useIntegrations();
  const integrationDefaultMethod =
    activeTelephonyIntegration?.additionalConfig?.defaultHandoffMethod;

  const FinalSchema = HandoffSchema.refine(
    values => {
      const id = defaultValues?.id;

      return !handoffs.some(
        handoff => handoff.id !== id && handoff.name === values.name,
      );
    },
    {
      path: ['name'],
      message: HANDOFF.modal.add.form.destinationInput.duplicateError,
    },
  ).transform(data => {
    const methodType =
      activeTelephonyIntegration?.additionalConfig?.defaultHandoffMethod ??
      data.methodType;

    const route =
      'route' in data &&
      !activeTelephonyIntegration?.additionalConfig?.noConfigurableRoute
        ? data.route
        : '';

    const integrationName =
      'integrationName' in data ? data.integrationName : '';

    const encryptionValue = 'encryption' in data ? data.encryption : undefined;

    const getSipConfig = () => {
      switch (methodType) {
        case 'invite':
          return {
            config: {
              $case: 'invite',
              value: {
                outboundEncryption: encryptionValue,
                outboundEndpoint: integrationName,
                phoneNumber: route,
              },
            },
          };
        case 'refer':
          return {
            config: {
              $case: 'refer',
              value: {
                phoneNumber: route,
              },
            },
          };
        case 'bye':
          return {
            config: {
              $case: 'bye',
              value: {},
            },
          };
        default:
          return {
            config: {
              $case: 'bye',
              value: {},
            },
          };
      }
    };

    const sipConfig = getSipConfig();
    const sipHeaders = !activeTelephonyIntegration?.additionalConfig
      ?.noConfigurableSIPHeaders
      ? {
          headers: Object.entries(data.sipHeaders).map(([key, value]) => ({
            key,
            value: value,
          })),
        }
      : {
          headers: [],
        };

    const res = streamSchema.safeParse({
      id: data.id || generateHandoffId(),
      name: data.name,
      description: data.description,
      sipConfig,
      sipHeaders,
      active: true,
      references: handoffReferences.createInitialState(),
    });

    if (res.error) {
      setErrors(res.error.issues);
    } else {
      setErrors([]);
    }
    return res.data;
  });

  const methods = useForm<ModalSchema, unknown, T>({
    resolver: zodResolver(FinalSchema),
    defaultValues: getDefaultValues(defaultValues, integrationDefaultMethod),
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
    getValues,
    setValue,
  } = methods;

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing deps are intentional
  useEffect(() => {
    // We want to reset form to default values when modal closes and with this modal that has weird behavior we can't do reset(defaultValues) without hacking, so this is the easies solution
    reset(getDefaultValues(defaultValues, integrationDefaultMethod));
    setShowCallout(false);

    // We need to override defaultMethod if necessary and dirty the form
    // to allow the user to instantly save the form on open.
    if (
      integrationDefaultMethod &&
      integrationDefaultMethod !== getValues('methodType')
    ) {
      setShowCallout(true);
      setValue('methodType', integrationDefaultMethod, { shouldDirty: true });
      setValue(
        'method',
        {
          value: integrationDefaultMethod,
          label: `${METHOD_TYPE_PREFIX} ${integrationDefaultMethod.toUpperCase()}`,
        },
        { shouldDirty: true },
      );
    }
  }, [rest.open, reset, defaultValues, integrationDefaultMethod]);

  const onSubmit: SubmitHandler<z.infer<T>> = data => {
    if (errors.length > 0) {
      return;
    }
    onConfirmClicked(data);
  };

  const handleCancelClicked = () => {
    onCancelButtonClick?.();
  };

  return (
    // TODO: Fix these any's
    <FormProvider methods={methods} onSubmit={onSubmit as any}>
      <ConfirmationModal
        {...rest}
        buttonText={readonly ? 'Close' : rest.buttonText}
        confirmDisabled={!isDirty && !readonly}
        calloutProps={
          showCallout
            ? {
                children: HANDOFF.modal.formHandoff.methodChangedCallout(
                  `${METHOD_TYPE_PREFIX} ${activeTelephonyIntegration?.additionalConfig?.defaultHandoffMethod?.toUpperCase() ?? ''}`,
                ),
                type: CalloutType.WARNING,
              }
            : undefined
        }
        content={
          <FormAddEditHandoffStreamModalContent
            errors={errors}
            readonly={readonly}
          />
        }
        skipCloseAndConfirm
        onCancelButtonClick={handleCancelClicked}
        onCancelDDActionName={getActionName('Handoffs', 'cancel', 'edit')}
        onClose={handleCancelClicked}
        onConfirmButtonClick={
          readonly ? handleCancelClicked : handleSubmit(onSubmit as any)
        }
      />
    </FormProvider>
  );
};
